/**
 * FN-FE-05 — Partnerek UI (lista, NAV-lookup, szerkesztő, címkék, kapcsolattartók)
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../../lib/api';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import ActionsDropdown from '../../components/ActionsDropdown';
import AddressForm, { emptyAddress } from '../../components/AddressForm';
import type { AddressData } from '../../components/AddressForm';
import ModulumBadge from '../../components/ui/ModulumBadge';

interface Partner {
  id: string; name: string; tax_number?: string;
  is_customer: boolean; is_supplier: boolean; is_archived: boolean;
  city?: string; network_tenant_id?: string | null;
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showIndividuals, setShowIndividuals] = useState(false);

  useFKeyBar([
    { key: 'F5', label: 'Új partner', action: () => setShowNewModal(true) },
  ]);

  async function loadPartners() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (!showIndividuals) params.set('kind', 'company');
      const data = await get(`/partners?${params}`);
      setPartners(data.data || data);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { loadPartners(); }, [showIndividuals]);
  useEffect(() => { const t = setTimeout(loadPartners, 300); return () => clearTimeout(t); }, [search]);

  return (
    <div>
      <PageHeader title="Partnerek" description="Vevők és beszállítók kezelése." actions={<Button onClick={() => setShowNewModal(true)}>+ Új partner</Button>} />

      {/* Szűrő-sáv */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <Input placeholder="Keresés név, adószám alapján..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--slate)', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
          <span>👤 Magánszemélyek</span>
          <button type="button" onClick={() => setShowIndividuals(v => !v)}
            style={{
              width: '36px', height: '20px', borderRadius: '10px', border: 'none', cursor: 'pointer', position: 'relative',
              background: showIndividuals ? '#2563eb' : '#cbd5e1', transition: 'background 0.2s',
            }}>
            <span style={{
              position: 'absolute', top: '2px', width: '16px', height: '16px', borderRadius: '50%',
              background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              left: showIndividuals ? '18px' : '2px', transition: 'left 0.2s',
            }} />
          </button>
        </label>
      </div>

      {/* Táblázat */}
      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : partners.length === 0 ? (
        <EmptyState icon="👥" title="Még nincsenek partnerek" description="Hozz létre egyet a + Új partner gombbal." action={<Button size="sm" onClick={() => setShowNewModal(true)}>+ Új partner</Button>} />
      ) : (
        <div className="bg-white rounded-xl border border-[var(--color-border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Típus</th>
                <th className="px-4 py-3">Város</th>
                <th className="px-4 py-3">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" style={{ cursor: 'pointer' }} onClick={() => navigate(`/partnerek/${p.id}`)}>
                  <td className="px-4 py-3">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '14px' }}>{p.kind === 'individual' ? '👤' : '🏢'}</span>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 700, color: 'var(--navy)' }}>
                          {p.name}
                          {p.network_tenant_id && <ModulumBadge />}
                        </div>
                        {p.tax_number && <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{p.tax_number}</div>}
                        {p.kind === 'individual' && !p.tax_number && <div style={{ fontSize: '10px', color: 'var(--slate)' }}>Magánszemély</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 space-x-1">
                    {p.is_customer && <Badge variant="success">Vevő</Badge>}
                    {p.is_supplier && <Badge variant="info">Beszállító</Badge>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{p.city || p.address_city || '—'}</td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <ActionsDropdown actions={[
                      { label: 'Megnyitás', icon: '👁', onClick: () => navigate(`/partnerek/${p.id}`) },
                      { label: 'Szerkesztés', icon: '✏️', onClick: () => navigate(`/partnerek/${p.id}`) },
                      { separator: true, label: '', onClick: () => {} },
                      { label: 'Új megrendelés', icon: '🛒', onClick: () => navigate(`/megrendelesek?partnerId=${p.id}&partnerName=${encodeURIComponent(p.name)}`) },
                      { label: 'Új számla', icon: '📄', onClick: () => navigate(`/szamlak?fromPartner=${p.id}&partnerName=${encodeURIComponent(p.name)}`) },
                      { separator: true, label: '', onClick: () => {} },
                      { label: 'Archiválás', icon: '🗄', onClick: async () => {
                        try { await post(`/partners/${p.id}/archive`, {}); loadPartners(); } catch { /* */ }
                      }, danger: true },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Új partner modal */}
      {showNewModal && <NewPartnerModal onClose={() => setShowNewModal(false)} onCreated={() => { setShowNewModal(false); loadPartners(); }} />}
    </div>
  );
}

// ── Új Partner Modal (NAV-lookup + Modulum hálózat flow) ──

interface NetworkResult {
  tenant_id: string;
  company_name: string;
  tax_number: string;
  community_vat_number?: string;
  address_postal_code: string;
  address_city: string;
  address_street_name: string;
  address_street_category?: string;
  address_house_number: string;
  address_floor?: string;
  address_door?: string;
  address_building?: string;
  address_staircase?: string;
  email?: string;
  bank_account_primary: string;
  website?: string;
}

function NewPartnerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [partnerKind, setPartnerKind] = useState<'company' | 'individual'>('company');
  const [step, setStep] = useState<'type' | 'lookup' | 'form'>('type');

  // NAV lookup
  const [taxNumber, setTaxNumber] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [navData, setNavData] = useState<Record<string, any> | null>(null);

  // Modulum hálózat keresés
  const [networkQuery, setNetworkQuery] = useState('');
  const [networkResults, setNetworkResults] = useState<NetworkResult[]>([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [dataSource, setDataSource] = useState<null | 'nav' | 'network'>(null);
  const [networkTenantId, setNetworkTenantId] = useState<string | null>(null);
  const networkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', taxNumber: '', euTaxNumber: '', isCustomer: true, isSupplier: false,
    address: { ...emptyAddress } as AddressData,
    bankAccountNumber: '', email: '', notes: '',
  });

  // Hálózat keresés debounce
  useEffect(() => {
    if (networkTimerRef.current) clearTimeout(networkTimerRef.current);
    if (networkQuery.length < 2) { setNetworkResults([]); return; }
    networkTimerRef.current = setTimeout(async () => {
      setNetworkLoading(true);
      try {
        const data = await get<{ results: NetworkResult[] }>(`/partners/network-search?q=${encodeURIComponent(networkQuery)}`);
        setNetworkResults(data.results || []);
      } catch { setNetworkResults([]); }
      setNetworkLoading(false);
    }, 300);
  }, [networkQuery]);

  function applyNetworkResult(r: NetworkResult) {
    setForm({
      name: r.company_name,
      taxNumber: r.tax_number,
      euTaxNumber: r.community_vat_number || '',
      isCustomer: true, isSupplier: false,
      address: {
        postalCode: r.address_postal_code || '',
        city: r.address_city || '',
        streetName: r.address_street_name || '',
        streetType: (r.address_street_category || '').toLowerCase(),
        houseNumber: r.address_house_number || '',
        building: r.address_building || '',
        staircase: r.address_staircase || '',
        floor: r.address_floor || '',
        door: r.address_door || '',
      },
      bankAccountNumber: r.bank_account_primary || '',
      email: r.email || '',
      notes: '',
    });
    setDataSource('network');
    setNetworkTenantId(r.tenant_id || null);
    setNetworkResults([]);
    setNetworkQuery('');
    setStep('form');
  }

  async function handleLookup() {
    setLookupLoading(true); setLookupError('');
    try {
      const taxDigits = taxNumber.replace(/-/g, '').slice(0, 8);
      const data = await post('/partners/lookup-tax-number', { taxNumber: taxDigits });
      if (data.found === false) { setLookupError(data.message || 'Az adószám a NAV nyilvántartásában nem található.'); return; }
      const d = data.data || data;
      setNavData(d);
      const navAddr = d.addresses?.[0] || d;
      const fullTaxNum = d.taxNumber || taxNumber;
      const rawStreetType = (navAddr.streetType || navAddr.publicPlaceCategory || '').toLowerCase();
      setForm({
        name: d.shortName || d.name || '', taxNumber: fullTaxNum,
        euTaxNumber: '', isCustomer: true, isSupplier: false,
        address: {
          postalCode: navAddr.postalCode || d.postalCode || '',
          city: (navAddr.city || d.city || '').replace(/^(\S)(\S+)/, (_: string, a: string, b: string) => a + b.toLowerCase()),
          streetName: (navAddr.streetName || d.street || '').replace(/^(\S)(\S+)/, (_: string, a: string, b: string) => a + b.toLowerCase()),
          streetType: rawStreetType,
          houseNumber: navAddr.houseNumber || d.houseNumber || '',
          building: navAddr.building || '',
          staircase: navAddr.staircase || '',
          floor: navAddr.floor || '',
          door: navAddr.door || '',
        },
        bankAccountNumber: '', email: '', notes: '',
      });
      setTaxNumber(fullTaxNum);
      setDataSource('nav');
      setStep('form');
    } catch { setLookupError('Hálózati hiba.'); } finally { setLookupLoading(false); }
  }

  // Auto-trigger NAV lookup after 8 digits
  useEffect(() => {
    const digits = taxNumber.replace(/-/g, '');
    if (digits.length === 8 && !lookupLoading && step === 'lookup') {
      const t = setTimeout(handleLookup, 500);
      return () => clearTimeout(t);
    }
  }, [taxNumber]);

  async function handleSave() {
    setSaving(true);
    try {
      const newPartner = await post<{ id: string }>('/partners', {
        name: form.name,
        kind: partnerKind,
        taxNumber: partnerKind === 'company' ? (form.taxNumber || undefined) : undefined,
        euTaxNumber: partnerKind === 'company' ? (form.euTaxNumber || undefined) : undefined,
        isCustomer: form.isCustomer,
        isSupplier: form.isSupplier,
        bankAccountNumber: form.bankAccountNumber || undefined,
        email: form.email || undefined,
        notes: form.notes || undefined,
        networkTenantId: networkTenantId || undefined,
        addresses: (form.address.city && form.address.streetName && form.address.postalCode) ? [{
          postalCode: form.address.postalCode,
          city: form.address.city,
          streetName: form.address.streetName,
          streetType: form.address.streetType || undefined,
          houseNumber: form.address.houseNumber || undefined,
          building: form.address.building || undefined,
          staircase: form.address.staircase || undefined,
          floor: form.address.floor || undefined,
          door: form.address.door || undefined,
          isPrimary: true,
        }] : undefined,
      });
      // Ha Modulum partner: hálózati kapcsolat automatikus kezdeményezése
      if (networkTenantId && newPartner?.id) {
        try {
          const role = form.isCustomer && form.isSupplier ? 'both' : form.isCustomer ? 'supplier' : 'customer';
          await post('/network/connect', { responderTenantId: networkTenantId, role, partnerId: newPartner.id });
        } catch { /* Ha már van kapcsolat, nem hiba */ }
      }
      onCreated();
    } catch (e: any) { console.error('[Partner save]', e); alert(e.message || 'Mentés sikertelen.'); } finally { setSaving(false); }
  }

  return (
    <div className="overlay show" style={{display:"flex",padding:"20px"}} onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-h">
          <h3>+ Új partner</h3>
          <button onClick={onClose} className="x">&times;</button>
        </div>

        <div className="modal-b">
          {/* Típusválasztó */}
          {step === 'type' && (
            <div className="space-y-4">
              <p style={{ fontSize: '13px', color: 'var(--slate)' }}>Milyen típusú partnert szeretnél létrehozni?</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <button onClick={() => { setPartnerKind('company'); setStep('lookup'); }}
                  style={{ padding: '20px 16px', borderRadius: 'var(--r)', border: '2px solid var(--line)', background: 'var(--white)', cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>🏢</div>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>Cég</div>
                  <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>Kft, Bt, Zrt, egyéni vállalkozó — adószámmal</div>
                </button>
                <button onClick={() => { setPartnerKind('individual'); setStep('form'); }}
                  style={{ padding: '20px 16px', borderRadius: 'var(--r)', border: '2px solid var(--line)', background: 'var(--white)', cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>👤</div>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>Magánszemély</div>
                  <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>Természetes személy — adószám nélkül</div>
                </button>
              </div>
            </div>
          )}

          {/* Lookup lépés — Modulum hálózat + NAV */}
          {step === 'lookup' && partnerKind === 'company' && (
            <div className="space-y-4">

              {/* Modulum hálózat keresés */}
              <div>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: 'var(--navy)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, letterSpacing: '.06em' }}>MODULUM</span>
                  Keresés a hálózatban
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    autoFocus
                    value={networkQuery}
                    onChange={e => setNetworkQuery(e.target.value)}
                    placeholder="Kezdd el gépelni a cég nevét..."
                    style={{ paddingRight: networkLoading ? 36 : undefined }}
                  />
                  {networkLoading && (
                    <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="2.5"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                  )}
                  {/* Eredmények dropdown */}
                  {networkResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                      background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--r)',
                      boxShadow: '0 8px 30px -8px rgba(20,35,63,.25)', marginTop: 4, overflow: 'hidden',
                    }}>
                      {networkResults.map((r, i) => (
                        <button
                          key={i}
                          onClick={() => applyNetworkResult(r)}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
                            padding: '10px 14px', border: 'none', background: 'none',
                            cursor: 'pointer', textAlign: 'left', borderBottom: i < networkResults.length - 1 ? '1px solid var(--line-2)' : 'none',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--mist)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        >
                          <div style={{
                            width: 34, height: 34, borderRadius: 8, background: 'var(--navy)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 800, fontSize: 13, flexShrink: 0,
                          }}>
                            {r.company_name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.company_name}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 2 }}>
                              {r.tax_number}
                              {r.address_city ? ` · ${r.address_postal_code} ${r.address_city}` : ''}
                            </div>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 700, flexShrink: 0, alignSelf: 'center' }}>
                            Modulum
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {networkQuery.length >= 2 && !networkLoading && networkResults.length === 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: '10px 14px', marginTop: 4, fontSize: 12, color: 'var(--slate)' }}>
                      Nincs találat a Modulum hálózatban
                    </div>
                  )}
                </div>
              </div>

              {/* Elválasztó */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                <span style={{ fontSize: 11, color: 'var(--slate)', fontWeight: 600 }}>VAGY NAV LEKÉRDEZÉS</span>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              </div>

              {/* NAV adószám lookup */}
              <div>
                <Input label="Adószám (8 jegy után auto-lekérdez)" value={taxNumber} onChange={e => setTaxNumber(e.target.value)} placeholder="12345678-2-43" />
                {lookupError && <div style={{ fontSize: '12px', color: 'var(--red)', marginTop: 6 }}>{lookupError}</div>}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Button onClick={handleLookup} loading={lookupLoading} disabled={taxNumber.replace(/-/g, '').length < 8}>Lekérdezés a NAV-tól</Button>
                <button onClick={() => setStep('form')} style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Manuális rögzítés →</button>
                <button onClick={() => setStep('type')} style={{ fontSize: '12px', color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer' }}>← Vissza</button>
              </div>
            </div>
          )}

          {/* Űrlap */}
          {step === 'form' && (
            <div className="space-y-4">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>{partnerKind === 'company' ? '🏢' : '👤'}</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>{partnerKind === 'company' ? 'Cég' : 'Magánszemély'}</span>
                <button onClick={() => { setStep('type'); setNavData(null); setDataSource(null); setForm(f => ({ ...f, name: '', taxNumber: '' })); }}
                  style={{ fontSize: '11px', color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: '4px' }}>Módosítás</button>
              </div>

              {dataSource === 'nav' && <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 'var(--r)', fontSize: '12px', color: '#166534' }}>✅ Adatok lekérve a NAV-tól. Felülírhatod.</div>}
              {dataSource === 'network' && <div style={{ padding: '8px 12px', background: '#eff6ff', borderRadius: 'var(--r)', fontSize: '12px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ background: 'var(--navy)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 3 }}>MODULUM</span> Adatok átvéve a hálózatból. Felülírhatod.</div>}

              <Input label={partnerKind === 'company' ? 'Cégnév *' : 'Teljes név *'} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={partnerKind === 'company' ? 'Példa Kft.' : 'Kovács János'} />

              {partnerKind === 'company' && (<>
                <Input label="Adószám" value={form.taxNumber} onChange={e => setForm(f => ({ ...f, taxNumber: e.target.value }))} placeholder="12345678-2-43" />
                {form.euTaxNumber && <Input label="Közösségi adószám" value={form.euTaxNumber} onChange={e => setForm(f => ({ ...f, euTaxNumber: e.target.value }))} placeholder="HU12345678" />}
              </>)}

              <AddressForm value={form.address} onChange={(addr) => setForm(f => ({ ...f, address: addr }))} />

              <Input label="E-mail" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder={partnerKind === 'company' ? 'info@ceg.hu' : 'janos@email.hu'} />

              {partnerKind === 'company' && (
                <Input label="Bankszámlaszám" value={form.bankAccountNumber} onChange={e => setForm(f => ({ ...f, bankAccountNumber: e.target.value }))} placeholder="12345678-12345678-12345678" />
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.isCustomer} onChange={e => setForm(f => ({ ...f, isCustomer: e.target.checked }))} style={{ accentColor: 'var(--navy)' }} /> Vevő
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.isSupplier} onChange={e => setForm(f => ({ ...f, isSupplier: e.target.checked }))} style={{ accentColor: 'var(--navy)' }} /> Beszállító
                </label>
              </div>

              <div>
                <label className="form-label">Megjegyzés</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="form-input" placeholder="Belső megjegyzés..." />
              </div>
            </div>
          )}
        </div>

        {(step === 'form' || step === 'lookup') && (
          <div style={{padding:"12px 20px",borderTop:"1px solid var(--line)",display:"flex",justifyContent:"flex-end",gap:"9px"}}>
            <Button variant="ghost" onClick={onClose}>Mégse</Button>
            {step === 'form' && <Button onClick={handleSave} loading={saving} disabled={!form.name}>Partner mentése</Button>}
          </div>
        )}
      </div>
    </div>
  );
}
